import axios, { AxiosInstance } from "axios";

import { msalService } from "./msal.service";
import { chunkArray } from "../lib/array";
import {
  AnalyzeCitationItem,
  AnalyzeLawCasesResponse,
  DocumentDetailResponse,
} from "../models/benchly.models";

// Service class
class BenchlyService {
  private baseURL: string;
  private axiosInstance: AxiosInstance;

  constructor() {
    // Get base URL from environment variable or use fallback
    const envBaseURL = process.env.REACT_APP_API_BASE_URL;

    if (!envBaseURL) {
      console.warn("REACT_APP_API_BASE_URL not set in environment, using fallback");
      this.baseURL =
        "https://benchlyv2-dev-caselaw-api-c7eeakhqadath0hm.southcentralus-01.azurewebsites.net/api";
    } else {
      // Remove trailing slash if present and add /api
      this.baseURL = `${envBaseURL.replace(/\/+$/, "")}/api`;
    }

    console.log("Benchly Service initialized with base URL:", this.baseURL);
    this.initializeAxiosInstance();
  }

  // Initialize or reinitialize the axios instance with interceptors
  private initializeAxiosInstance(): void {
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      timeout: 60000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add request interceptor for authentication
    this.axiosInstance.interceptors.request.use(async (config) => {
      const token = await msalService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  async analyzeLawCasesFuzzy(request: AnalyzeCitationItem[]): Promise<AnalyzeLawCasesResponse> {
    try {
      // Split requests into chunks of 20 to avoid payload and timeout issues
      const chunks = chunkArray(request, 20);
      const promises = chunks.map((chunk) =>
        this.axiosInstance.post<AnalyzeLawCasesResponse>("/Document/resolve-citations-fuzzy", chunk)
      );

      const responses = await Promise.all(promises);
      const allResults = responses.flatMap((res) =>
        res.status === 200 && res.data && Array.isArray(res.data.results) ? res.data.results : []
      );

      return { results: allResults };
    } catch (error) {
      console.error("Fuzzy analyze law cases failed:", error);
      throw error;
    }
  }

  async analyzeLawCasesExact(request: AnalyzeCitationItem[]): Promise<AnalyzeLawCasesResponse> {
    try {
      // Split requests into chunks of 20 to avoid payload and timeout issues
      const chunks = chunkArray(request, 20);
      const promises = chunks.map((chunk) =>
        this.axiosInstance.post<AnalyzeLawCasesResponse>("/Document/resolve-citations", chunk)
      );

      const responses = await Promise.all(promises);
      const allResults = responses.flatMap((res) =>
        res.status === 200 && res.data && Array.isArray(res.data.results) ? res.data.results : []
      );

      return { results: allResults };
    } catch (error) {
      console.error("Analyze law cases (exact) failed:", error);
      throw error;
    }
  }

  async getDocumentById(docId: string): Promise<DocumentDetailResponse> {
    try {
      const response = await this.axiosInstance.get<DocumentDetailResponse>(`/Document/${docId}`);
      return response.data;
    } catch (error) {
      console.error("Get document by ID failed:", error);
      throw error;
    }
  }

  // Change the base URL (for switching between environments)
  setBaseURL(baseURL: string): void {
    this.baseURL = baseURL;
    this.initializeAxiosInstance();
  }

  openDocumentAndSearchCitation(docId: string): void {
    // Attempt to open the related document in a new browser tab/window.
    // This provides a concrete behavior for UI interactions that invoke this method.
    try {
      // Only attempt navigation in a browser-like environment.
      if (typeof window !== "undefined" && typeof window.open === "function") {
        const base = this.baseURL || "";
        const url = `${base.replace(/\/+$/, "")}/documents/${encodeURIComponent(docId)}`;
        window.open(url, "_blank");
      } else {
        // In non-browser environments, log a clear warning instead of silently doing nothing.
        // This makes it obvious that navigation is not supported here.
        console.warn(
          `openDocumentAndSearchCitation: navigation is not supported in the current environment (docId=${docId}).`
        );
      }
    } catch (error) {
      console.error("Failed to open document for citation:", docId, error);
    }
  }
}

// Export singleton instance
export const benchlyService = new BenchlyService();
export default benchlyService;
